import { CustomPagination } from 'components/helpers/AlgoliaSearch/CustomPagination';
import { Table, Thead, Tr, Tbody } from 'react-super-responsive-table';
import TcSearchHeadings from './_SearchHeadings';
import TcHits from 'components/helpers/AlgoliaSearch/TcHits';
import useScreenWidth from 'lib/util/useScreenWidth';
import { classnames } from 'tailwindcss-classnames';
import { useCallback, useMemo, useRef, useState } from 'react';
import { env } from 'lib/env';
import { logWarning } from 'lib/util/errors';
import { useSortBy } from 'react-instantsearch-hooks-web';
import { buildVirtualSortIndexName, isVirtualSortIndex } from 'lib/util/algolia';

const StableSortItems = [
  { label: 'sortDate', value: `${env.ALGOLIA_INDEX_NAME}_date_desc` },
  { label: 'sortName', value: `${env.ALGOLIA_INDEX_NAME}_name_asc` },
  { label: 'sortNameDesc', value: `${env.ALGOLIA_INDEX_NAME}_name_desc` },
  { label: 'sortCity', value: `${env.ALGOLIA_INDEX_NAME}_city_asc` },
  { label: 'sortCityDesc', value: `${env.ALGOLIA_INDEX_NAME}_city_desc` },
  { label: 'sortState', value: `${env.ALGOLIA_INDEX_NAME}_state_asc` },
  { label: 'sortStateDesc', value: `${env.ALGOLIA_INDEX_NAME}_state_desc` },
  { label: 'sortAllos', value: `${env.ALGOLIA_INDEX_NAME}_allos_desc` },
  { label: 'sortAllosAsc', value: `${env.ALGOLIA_INDEX_NAME}_allos_asc` },
  { label: 'sortProgramType', value: `${env.ALGOLIA_INDEX_NAME}_programtype_asc` },
  { label: 'sortProgramTypeDesc', value: `${env.ALGOLIA_INDEX_NAME}_programtype_desc` },
  { label: 'sortSurvivorship', value: `${env.ALGOLIA_INDEX_NAME}_survivorship_asc` },
  { label: 'sortSurvivorshipDesc', value: `${env.ALGOLIA_INDEX_NAME}_survivorship_desc` },
  { label: 'defaultIndex', value: `${env.ALGOLIA_INDEX_NAME}` },
];

// Disease sort indices are per-attribute virtual indices (see lib/util/algolia.ts)
// getSortItem accepts them without requiring an explicit entry.
export const getSortItem = (index: string) => {
  const normalMatch = index.toLocaleLowerCase() ?? env.ALGOLIA_INDEX_NAME;
  if (isVirtualSortIndex(normalMatch)) {
    return { label: 'diseaseTotal', value: normalMatch };
  }
  let match = StableSortItems.find((item) => item.value === normalMatch);
  if (!match) {
    logWarning(`Request to sort by missing index ${normalMatch}, defaulting to relevance.`);
    match =
      StableSortItems.find((item) => item.value === env.ALGOLIA_INDEX_NAME) ?? StableSortItems[0];
  }
  return match;
};

const deriveSortIndicator = (sortByParam: string): { columName: string; isAscending: boolean } => {
  if (!sortByParam) return { columName: 'Date', isAscending: false };

  // Virtual disease sort: dt_{id}_{asc|desc}
  const virtualMatch = /^(dt_.+)_(asc|desc)$/.exec(sortByParam);
  if (virtualMatch) {
    return { columName: virtualMatch[1], isAscending: virtualMatch[2] === 'asc' };
  }
  // Regular sort: {column}_{asc|desc}
  const lastUnderscore = sortByParam.lastIndexOf('_');
  if (lastUnderscore > 0) {
    const column = sortByParam.slice(0, lastUnderscore);
    const direction = sortByParam.slice(lastUnderscore + 1);
    const columnMap: Record<string, string> = {
      date: 'Date',
      name: 'name',
      city: 'city',
      state: 'state',
      allos: 'allos',
      programtype: 'programType',
      survivorship: 'survivorship',
    };
    const mappedColumn = columnMap[column.toLowerCase()] ?? column;
    return { columName: mappedColumn, isAscending: direction === 'asc' };
  }
  return { columName: 'Date', isAscending: false };
};
const getInitialSortFromUrl = (): string => {
  if (typeof window === 'undefined') return '';
  const params = new URLSearchParams(window.location.search);
  return params.get('sortBy') ?? '';
};

const TcHitsSection = ({ txId, nbHits }: { txId: string; nbHits: number }): JSX.Element => {
  const currentScreenWidth = useScreenWidth();
  const extraItemsRef = useRef<Array<{ label: string; value: string }>>([]);
  const sortByItems = useMemo(() => {
    if (txId) {
      const ascIndex = buildVirtualSortIndexName(env.ALGOLIA_INDEX_NAME, `dt_${txId}`, true);
      const descIndex = buildVirtualSortIndexName(env.ALGOLIA_INDEX_NAME, `dt_${txId}`, false);
      const alreadyAdded = extraItemsRef.current.some((i) => i.value === ascIndex);
      if (!alreadyAdded) {
        extraItemsRef.current = [
          ...extraItemsRef.current,
          { label: 'diseaseTotalAsc', value: ascIndex },
          { label: 'diseaseTotalDesc', value: descIndex },
        ];
      }
    }
    return [...StableSortItems, ...extraItemsRef.current];
  }, [txId]);

  const { refine, currentRefinement } = useSortBy({
    items: sortByItems,
  });

  const [sortIndicator, setIndicator] = useState(() => {
    const urlSort = getInitialSortFromUrl();
    if (urlSort) {
      const indicator = deriveSortIndicator(urlSort);
      // If sort is a dt_ column, ensure it matches the current txId
      if (indicator.columName.startsWith('dt_') && txId && indicator.columName !== `dt_${txId}`) {
        if (typeof window !== 'undefined') {
          const url = new URL(window.location.href);
          const sortBy = url.searchParams.get('sortBy');
          if (sortBy && sortBy.startsWith('dt')) {
            url.searchParams.set('sortBy', `dt_${txId}`);
          }
          window.history.replaceState({}, '', url.toString());
        }
        return { columName: `dt_${txId}`, isAscending: indicator.isAscending };
      }
      return indicator;
    }
    return { columName: 'Date' as string, isAscending: false };
  });
  const prevTxId = useRef(txId);

  const sorting = useCallback(
    (column: string, isAscending: boolean) => {
      const sortIndex =
        txId && column === `dt_${txId}`
          ? buildVirtualSortIndexName(env.ALGOLIA_INDEX_NAME, column, isAscending)
          : `${env.ALGOLIA_INDEX_NAME}_${column}_${isAscending ? 'asc' : 'desc'}`;
      refine(getSortItem(sortIndex).value);
      setIndicator({ columName: column, isAscending });
    },
    [txId, refine]
  );

  // Handle user changing disease while sorted by a dt_ column
  if (txId !== prevTxId.current) {
    const isOnDiseaseSort =
      isVirtualSortIndex(currentRefinement) || sortIndicator.columName.startsWith('dt_');
    prevTxId.current = txId;

    if (!txId && isOnDiseaseSort) {
      // Disease cleared while on dt_ column — reset sort to default
      refine(env.ALGOLIA_INDEX_NAME);
      setIndicator({ columName: 'Date', isAscending: false });
    } else if (txId && isOnDiseaseSort) {
      // Disease changed while on dt_ column — update to new disease sort
      const newIndex = buildVirtualSortIndexName(
        env.ALGOLIA_INDEX_NAME,
        `dt_${txId}`,
        sortIndicator.isAscending
      );
      refine(getSortItem(newIndex).value);
      setIndicator({ columName: `dt_${txId}`, isAscending: sortIndicator.isAscending });
    }
  }

  return (
    <>
      {currentScreenWidth > 1023 && (
        <Table className={classnames('max-w-[1024px]')}>
          <Thead>
            <Tr className={classnames('text-left', 'font-bold')}>
              {nbHits > 0 && (
                <TcSearchHeadings sortTable={sorting} sortIndicator={sortIndicator} txId={txId} />
              )}
            </Tr>
          </Thead>
          <Tbody>
            <TcHits txId={txId} />
          </Tbody>
        </Table>
      )}
      {currentScreenWidth < 1024 && <TcHits txId={txId} />}

      <div>
        <CustomPagination padding={1} />
      </div>
    </>
  );
};

export default TcHitsSection;
